import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Lock, LogOut, X, AlertCircle, ChevronDown, ArrowLeft, Clock, AlertTriangle, UserCog } from "lucide-react";
import { getRole, logoutUser } from "../../utils/authService";
import { apiGet, apiPut } from "../../utils/api";
import "./topnav.css";
import ThemeToggle from "../ThemeToggle.jsx";
import { toast } from "../../utils/toast";
import { MyProfileView } from "../profile/RoleAccounts.jsx";
import CommandPalette from "./CommandPalette.jsx";

/* ── Route-based back button configuration ── */
const BACK_BUTTON_MAP = {
    "/usb": { label: "Back to Monitoring", to: "/monitoring" },
    "/url-tracker": { label: "Back to Monitoring", to: "/monitoring" },
    "/file-monitor": { label: "Back to Monitoring", to: "/monitoring" },
    "/file-uploads": { label: "Back to Monitoring", to: "/monitoring" },
    "/role-accounts": { label: "Back to Settings", to: "/settings" },
    "/cfo-salary-config": { label: "Back to Settings", to: "/settings" },
};

// These two render icon-only (+ hover tooltip) instead of the text-labeled
// pill the other BACK_BUTTON_MAP routes use — both are Settings quick-link
// pages (Accounts/Salaries), reached one level deeper than the rest.
const ICON_ONLY_BACK_ROUTES = new Set(["/role-accounts", "/cfo-salary-config"]);

const TopNav = ({ sidebarOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const name = sessionStorage.getItem("name") || "User";
    const email = sessionStorage.getItem("email") || "";
    const displayRole = sessionStorage.getItem("displayRole") || "";
    // Download Account is a single-purpose seeded login for grabbing the agent
    // installer — it has no use for productivity/security notifications.
    // Fall back to matching the email prefix because accounts seeded before the
    // displayRole field existed never got it backfilled in the database.
    const isDownloadOnlyAccount = displayRole === "Download Account" || email.toLowerCase().startsWith("download@");
    const role = getRole();

    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    // Whether the tenant has any active/trial subscription — mirrors sidenav.jsx's
    // hasActiveSubscription. Only meaningful for the tenant owner ("client"); other
    // roles' access is governed by allowedViews, not billing, so they're unaffected.
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

    useEffect(() => {
        if (role !== "client") return;
        let isMounted = true;
        const fetchSubscriptionStatus = async () => {
            try {
                const res = await apiGet("/api/features");
                if (res.ok && isMounted) {
                    const data = await res.json();
                    setHasActiveSubscription(!!String(data?.currentPlanId || "").trim());
                }
            } catch (err) {
                console.error("[TopNav] Failed to fetch subscription status", err);
            }
        };
        void fetchSubscriptionStatus();
        // TopNav stays mounted across route changes (unlike sidenav's per-nav-item
        // checks), so a plan activated on the Billing page wouldn't otherwise be
        // picked up until a hard refresh. Re-check on the same signals sidenav.jsx
        // uses to refresh its own feature state: polling, tab focus, and the
        // "features:updated" event BillingPage/sidenav broadcast after a plan change.
        const interval = setInterval(() => void fetchSubscriptionStatus(), 10000);
        window.addEventListener("focus", fetchSubscriptionStatus);
        window.addEventListener("features:updated", fetchSubscriptionStatus);
        return () => {
            isMounted = false;
            clearInterval(interval);
            window.removeEventListener("focus", fetchSubscriptionStatus);
            window.removeEventListener("features:updated", fetchSubscriptionStatus);
        };
    }, [role]);

    const [maintConfig, setMaintConfig] = useState(null);
    const [maintComment, setMaintComment] = useState("");
    const [maintStatus, setMaintStatus] = useState({ type: "none", text: "", timeLeft: 0 });
    const [isMaintDropdownOpen, setIsMaintDropdownOpen] = useState(false);
    const maintRef = useRef(null);

    // Fetch maintenance settings (use authenticated endpoint since user is logged in)
    const fetchMaintenance = async () => {
        try {
            console.log("[TopNav Maint] Fetching maintenance settings...");
            const res = await apiGet("/api/system-config/public");
            console.log("[TopNav Maint] Fetch response status:", res.status);
            if (res.ok) {
                const data = await res.json();
                console.log("[TopNav Maint] Received data:", data);
                if (data.success) {
                    const parsedStart = data.maintenanceStart ? new Date(data.maintenanceStart) : null;
                    const parsedEnd = data.maintenanceEnd ? new Date(data.maintenanceEnd) : null;
                    console.log("[TopNav Maint] Parsed dates - Start:", parsedStart, "End:", parsedEnd, "Enabled:", data.maintenanceEnabled);
                    setMaintConfig({
                        maintenanceStart: parsedStart,
                        maintenanceEnd: parsedEnd,
                        maintenanceEnabled: !!data.maintenanceEnabled,
                    });
                    setMaintComment(data.maintenanceComment || "");
                }
            } else {
                console.warn("[TopNav Maint] Non-ok response from public system config:", res.statusText);
            }
        } catch (err) {
            console.error("[TopNav Maint] Failed to load maintenance config in topnav", err);
        }
    };

    useEffect(() => {
        void fetchMaintenance();
        const interval = setInterval(fetchMaintenance, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, []);

    // Countdown timer tick logic
    useEffect(() => {
        console.log("[TopNav Maint] Config changed:", maintConfig);
        if (!maintConfig || !maintConfig.maintenanceEnabled) {
            console.log("[TopNav Maint] Maintenance disabled or no config.");
            setMaintStatus({ type: "none", text: "", timeLeft: 0 });
            return;
        }

        const tick = () => {
            const now = new Date().getTime();
            const start = maintConfig.maintenanceStart ? maintConfig.maintenanceStart.getTime() : null;
            const end = maintConfig.maintenanceEnd ? maintConfig.maintenanceEnd.getTime() : null;

            if (!start || !end) {
                setMaintStatus({ type: "none", text: "", timeLeft: 0 });
                return;
            }

            if (now < start) {
                const diff = start - now;
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const secs = Math.floor((diff % (1000 * 60)) / 1000);

                const pad = (n) => String(n).padStart(2, "0");

                if (diff < 24 * 60 * 60 * 1000) {
                    const statusVal = {
                        type: "upcoming",
                        text: `Maintenance in ${pad(hours)}:${pad(mins)}:${pad(secs)}`,
                        timeLeft: diff,
                    };
                    setMaintStatus(statusVal);
                } else {
                    const statusVal = {
                        type: "upcoming-far",
                        text: `Maintenance scheduled`,
                        timeLeft: diff,
                    };
                    setMaintStatus(statusVal);
                }
            } else if (now >= start && now < end) {
                const diff = end - now;
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const secs = Math.floor((diff % (1000 * 60)) / 1000);

                const pad = (n) => String(n).padStart(2, "0");

                const statusVal = {
                    type: "active",
                    text: `Under Maintenance (${pad(hours)}:${pad(mins)}:${pad(secs)} left)`,
                    timeLeft: diff,
                };
                setMaintStatus(statusVal);
            } else {
                setMaintStatus({ type: "none", text: "", timeLeft: 0 });
            }
        };

        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [maintConfig]);

    // Close maintenance dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (maintRef.current && !maintRef.current.contains(e.target)) {
                setIsMaintDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passError, setPassError] = useState("");
    const [updating, setUpdating] = useState(false);

    const profileRef = useRef(null);

    const initial = name?.charAt(0)?.toUpperCase() || "U";

    // Determine if we need a back button for the current route.
    // /role-accounts renders two very different things depending on role (see RoleAccounts.jsx):
    // the admin/client "Manage Accounts" page (reached from Settings, where "Back to Settings"
    // is correct) vs. an employee/manager's own pending-setup dashboard (never reached from
    // Settings, and they typically can't access /settings at all) — suppress it for the latter.
    const rawBackConfig = BACK_BUTTON_MAP[location.pathname] || null;
    const backConfig =
        rawBackConfig && location.pathname === "/role-accounts" && (role === "employee" || role === "manager")
            ? null
            : rawBackConfig;

    // Close profile dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = () => {
        logoutUser();
        navigate("/login");
    };

    const openChangePassword = () => {
        setIsProfileOpen(false);
        setIsChangePasswordOpen(true);
    };

    const openProfileModal = () => {
        setIsProfileOpen(false);
        setIsProfileModalOpen(true);
    };

    const closeProfileModal = () => {
        setIsProfileModalOpen(false);
    };

    const closeChangePassword = () => {
        setIsChangePasswordOpen(false);
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPassError("");
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPassError("");

        if (newPassword !== confirmPassword) {
            return setPassError("New passwords do not match");
        }
        if (newPassword.length < 6) {
            return setPassError("Password must be at least 6 characters");
        }

        setUpdating(true);
        try {
            const res = await apiPut("/api/auth/change-password", {
                oldPassword,
                newPassword,
            });
            const data = await res.json();
            if (res.ok) {
                toast.success("Password changed successfully.");
                closeChangePassword();
            } else {
                setPassError(data.message || "Failed to update password");
                toast.error(data.message || "Password update failed.");
            }
        } catch (err) {
            setPassError("Server error. Please try again later.");
            toast.error("Failed to connect to server.");
        } finally {
            setUpdating(false);
        }
    };

    return (
        <>
            <header className={`topnav ${sidebarOpen ? "shifted" : ""}`}>
                <div className="topnav-content">
                    <div className="topnav-left">
                        {backConfig && (
                            ICON_ONLY_BACK_ROUTES.has(location.pathname) ? (
                                <button
                                    className="gc-icon-btn"
                                    onClick={() => navigate(backConfig.to)}
                                    id="topnav-back"
                                    data-tip={backConfig.label}
                                    aria-label={backConfig.label}
                                >
                                    <ArrowLeft size={16} />
                                </button>
                            ) : (
                                <button
                                    className="topnav-back-btn"
                                    onClick={() => navigate(backConfig.to)}
                                    id="topnav-back"
                                >
                                    <ArrowLeft size={16} />
                                    {backConfig.label}
                                </button>
                            )
                        )}
                        <CommandPalette />
                    </div>

                    {/* Right side: Bell + Profile */}
                    <div className="topnav-right">
                        {/* Maintenance Timer Alert */}
                        {maintStatus.type !== "none" && (
                            <div className="topnav-maint-wrapper" ref={maintRef}>
                                <button
                                    className={`topnav-maint-btn ${maintStatus.type}`}
                                    onClick={() => setIsMaintDropdownOpen(prev => !prev)}
                                    aria-label="Maintenance Info"
                                    title={maintStatus.text}
                                >
                                    <Clock size={16} />
                                    <span className="topnav-maint-ticker-text">{maintStatus.text}</span>
                                </button>

                                {isMaintDropdownOpen && (
                                    <div className="topnav-maint-dropdown">
                                        <div className={`topnav-maint-dropdown-header ${maintStatus.type}`}>
                                            <AlertTriangle size={16} />
                                            <span>Scheduled Maintenance</span>
                                        </div>
                                        <div className="topnav-maint-dropdown-body">
                                            <p className="topnav-maint-desc">
                                                {maintComment || (maintStatus.type === "active"
                                                    ? "The system is currently undergoing a scheduled maintenance break. Some real-time services might be temporarily unavailable."
                                                    : "A system-wide maintenance break has been scheduled. Please complete any active tasks before the maintenance starts.")}
                                            </p>
                                            <div className="topnav-maint-time-row">
                                                <span>Starts:</span>
                                                <strong>{maintConfig?.maintenanceStart ? new Date(maintConfig.maintenanceStart).toLocaleString("en-IN") : "-"}</strong>
                                            </div>
                                            <div className="topnav-maint-time-row">
                                                <span>Ends:</span>
                                                <strong>{maintConfig?.maintenanceEnd ? new Date(maintConfig.maintenanceEnd).toLocaleString("en-IN") : "-"}</strong>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Notification Bell */}
                        <ThemeToggle />
                        {!isDownloadOnlyAccount && (role !== "client" || hasActiveSubscription) && (
                            <button
                                className="topnav-bell-btn"
                                onClick={() => window.dispatchEvent(new CustomEvent("toggle-notifications"))}
                                aria-label="Notifications"
                                title="Notifications"
                                id="topnav-bell"
                            >
                                <Bell size={20} />
                            </button>
                        )}

                        {/* Profile Avatar */}
                        <div className="topnav-profile-wrapper" ref={profileRef}>
                            <button
                                className="topnav-avatar-btn"
                                onClick={() => setIsProfileOpen((prev) => !prev)}
                                aria-label="User profile"
                                id="topnav-profile"
                            >
                                <span className="topnav-avatar-circle">{initial}</span>
                                <ChevronDown size={14} className={`topnav-chevron ${isProfileOpen ? "open" : ""}`} />
                            </button>

                            {/* Dropdown */}
                            {isProfileOpen && (
                                <div className="topnav-dropdown" id="topnav-dropdown">
                                    <div className="topnav-dropdown-header">
                                        <span className="topnav-dropdown-avatar">{initial}</span>
                                        <div>
                                            <div className="topnav-dropdown-name">{name}</div>
                                            <div className="topnav-dropdown-email">{email}</div>
                                        </div>
                                    </div>
                                    <div className="topnav-dropdown-divider" />
                                    {!isDownloadOnlyAccount && (
                                        <button
                                            className="topnav-dropdown-item"
                                            onClick={openProfileModal}
                                            id="topnav-profile"
                                        >
                                            <UserCog size={16} />
                                            Profile
                                        </button>
                                    )}
                                    <button
                                        className="topnav-dropdown-item"
                                        onClick={openChangePassword}
                                        id="topnav-change-password"
                                    >
                                        <Lock size={16} />
                                        Change Password
                                    </button>
                                    <button
                                        className="topnav-dropdown-item logout"
                                        onClick={handleLogout}
                                        id="topnav-logout"
                                    >
                                        <LogOut size={16} />
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* ── Change Password Modal ── */}
            {isChangePasswordOpen && (
                <div className="topnav-modal-overlay" onClick={closeChangePassword}>
                    <div className="topnav-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="topnav-modal-header">
                            <h3><Lock size={18} /> Change Password</h3>
                            <button className="topnav-modal-close" onClick={closeChangePassword}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="topnav-modal-body">
                            {passError && (
                                <div className="topnav-modal-error">
                                    <AlertCircle size={16} /> {passError}
                                </div>
                            )}
                            <form onSubmit={handlePasswordChange}>
                                <div className="topnav-form-group">
                                    <label>Current Password</label>
                                    <input
                                        type="password"
                                        placeholder="Enter current password"
                                        value={oldPassword}
                                        onChange={(e) => setOldPassword(e.target.value)}
                                        required
                                        autoFocus
                                    />
                                </div>
                                <div className="topnav-form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        placeholder="Minimum 6 characters"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="topnav-form-group">
                                    <label>Confirm New Password</label>
                                    <input
                                        type="password"
                                        placeholder="Repeat new password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="topnav-modal-footer">
                                    <button type="button" className="topnav-btn-cancel" onClick={closeChangePassword}>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="topnav-btn-primary"
                                        disabled={updating || !oldPassword || !newPassword || !confirmPassword}
                                    >
                                        {updating ? "Processing..." : "Update Password"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Profile Modal ── */}
            {isProfileModalOpen && (
                <div className="topnav-modal-overlay" onClick={closeProfileModal}>
                    <div className="topnav-modal topnav-modal-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="topnav-modal-header">
                            <h3><UserCog size={18} /> Profile</h3>
                            <button className="topnav-modal-close topnav-modal-close-mild" onClick={closeProfileModal}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="topnav-modal-body">
                            <MyProfileView isModal />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TopNav;
